import { Directive, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { RequiredDynamicItemBaseComponent } from '../dynamic-item';
import moment from 'moment';
import { LogService } from '@ups/xplat/core';
import { IDynamicModelOptions } from '../../models/dynamic.interface';

@Directive()
export abstract class DynamicDateBaseComponent
  extends RequiredDynamicItemBaseComponent
  implements OnInit
{
  @Output() valueChange = new EventEmitter<string | null>();
  selectedDate: string;
  dateFormat = 'MMM D, yyyy';
  apiDateFormat = 'YYYY-MM-DDTHH';
  emptyDisplay = '---';
  protected log = inject(LogService);
  private pendingDateUpdateTimeoutId: number | null = null;

  /**
   * Compute the date display string, preferring the LIVE form-control value over
   * the static config.value snapshot. WI 32158: dynamicFormResetState fires on a
   * genuine clear AND spuriously on some relayout passes (signature-pad
   * rotation-back / scroll on the HSE Sign In Sheet). A user pick writes the
   * control (applyDateValue -> updateValue), never config.value, so a display
   * refresh that read config.value blanked a date whose control value was still
   * set. Control value is parsed with the api format applyDateValue wrote it in;
   * config.value is the initial default so a genuine reset still shows it.
   */
  computeDisplayDate(): string {
    const controlValue = this.group?.get(this.config?.formControlName)?.value;
    if (controlValue) {
      return moment(<string>controlValue, this.apiDateFormat).format(
        this.dateFormat
      );
    }
    if (this.config?.value) {
      return moment(new Date(<string>this.config.value)).format(
        this.dateFormat
      );
    }
    return this.emptyDisplay;
  }

  updateValue(value: string | null, targetFieldName?: string) {
    super.updateValue(value, targetFieldName);

    if (this.config?.options?.updateOnValueChange) {
      this.config?.options?.updateOnValueChange.forEach((selectionConfig) => {
        if (selectionConfig.formControlName) {
          const options: IDynamicModelOptions = {};
          if (selectionConfig.validation === 'minDate' && value) {
            options.minDate = this.getDateFromValue(value);
          }
          // `IDynamicUpdateOnValueChangeValidationTypes` has declared 'maxDate' alongside
          // 'minDate' all along while only the minDate branch existed, so a config asking for
          // maxDate type-checked and was then silently ignored. Zero configs in the repo used it
          // (verified before adding this), so implementing it changes no existing behaviour.
          if (selectionConfig.validation === 'maxDate' && value) {
            options.maxDate = this.getDateFromValue(value);
          }
          this.dynamicService?.itemTargetUpdate$.next({
            formControlName: selectionConfig.formControlName,
            value,
            type: 'date',
            options,
          });
        }
      });
    }
  }

  updateDateValue(value: string | number | Date) {
    if (this.pendingDateUpdateTimeoutId != null) {
      this.win.clearTimeout(this.pendingDateUpdateTimeoutId);
    }

    this.pendingDateUpdateTimeoutId = this.win.setTimeout(() => {
      this.pendingDateUpdateTimeoutId = null;
      this.applyDateValue(value);
    });
  }

  getDateFromValue(value: string) {
    const minDate = value.split('T')[0];
    const dateParts = minDate
      .split('-')
      .map((datePart) => parseInt(datePart, 10));
    const year = dateParts[0];
    const month = dateParts[1];
    const day = dateParts[2];
    return new Date(year, month - 1, day);
  }

  private applyDateValue(value: string | number | Date) {
    const datetime = moment(value);
    if (this.config?.options?.minDate) {
      const minDate = moment(this.config?.options?.minDate);
      // console.log('datetime:', datetime);
      // console.log('minDate:', minDate);
      if (datetime.isBefore(minDate)) {
        this.win.alert({
          title: 'Invalid Date Selection',
          message: `${this.config.label} cannot be less than ${minDate.format(
            this.dateFormat
          )}`,
          okButtonText: 'Ok',
        });
        return;
      }
    }
    if (this.config?.options?.maxDate) {
      const maxDate = moment(this.config?.options?.maxDate);
      // 🔴 Day granularity, deliberately NOT the instant. A max is a local midnight, so a bare
      // `isAfter(maxDate)` would reject the final allowed day at any time past 00:00, i.e. reject
      // almost every real selection ON the boundary day. The minDate branch above needs no
      // granularity because its off-by-one falls the safe way (a 09:00 pick is not before
      // midnight); the two are asymmetric on purpose, not by oversight.
      if (datetime.isAfter(maxDate, 'day')) {
        this.win.alert({
          title: 'Invalid Date Selection',
          message: `${this.config.label} cannot be more than ${maxDate.format(
            this.dateFormat
          )}`,
          okButtonText: 'Ok',
        });
        return;
      }
    }
    this.selectedDate = datetime.format(this.dateFormat);

    const apiFormatValue = datetime.format(this.apiDateFormat);
    this.updateValue(apiFormatValue);
    this.valueChange.emit(apiFormatValue);
    this.log.debug(apiFormatValue);
  }
}
